import { useEffect, useState } from 'react'
import { cva, VariantProps } from 'class-variance-authority'

import { RecurlyPayment } from 'api'
import { useRecurly } from 'lib/recurly'
import { useCheckout, useUpdate } from 'features/checkout/hooks'
import { Button } from 'components/ui/buttons'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { cn } from 'utils/classMerge'
import { useGrowthBookFeature } from 'context/growthbook-context'
import { growthbookFeatures } from 'utils/growthbook'
import { useContextSave } from 'hooks/use-context-save'

import { PaymentMethodWrapper } from '../payment-method-wrapper'
import { RecurlyContext } from '../../providers/index'
import { CHECKOUT_SUBMIT_TYPE } from '../form/consts'

import { PayPalLogo } from './logo'

const buttonVariants = cva(
  'tw-m-0 tw-flex tw-h-[42px] tw-min-w-[120px] tw-items-center tw-justify-center !tw-rounded-md tw-px-4 tw-py-[11px] tw-normal-case',
  {
    variants: {
      variant: {
        default: 'tw-bg-sky-500 disabled:tw-bg-sky-500 disabled:tw-opacity-50',
        yellow:
          'disabled:tw-bg-abmer-300 tw-bg-yellow-500 disabled:tw-opacity-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface PayPalPaymentProps extends VariantProps<typeof buttonVariants> {
  className?: string
  theme?: 'white' | 'blue'
  source?: string
  checkoutSuccessSource: string
  testId?: string
}

type PayPalInstance = {
  start: () => void
  on: (event: string, callback: (data?: unknown) => void) => void
  off: (event: string, callback: (data?: unknown) => void) => void
}

const isRecurlyPayment = (data: unknown): data is RecurlyPayment => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    typeof (data as RecurlyPayment).id === 'string'
  )
}

const isPayPalError = (
  data: unknown,
): data is { code: string; message: string } => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'code' in data &&
    'message' in data &&
    typeof (data as { code: string; message: string }).code === 'string' &&
    typeof (data as { code: string; message: string }).message === 'string'
  )
}

export const PayPal = ({
  className,
  variant,
  theme,
  source,
  testId,
  checkoutSuccessSource,
}: PayPalPaymentProps) => {
  const payPalDisabledFeature = useGrowthBookFeature(
    growthbookFeatures.PAYPAL_DISABLED_REGIONAL,
  )
  const context = useContextSave(RecurlyContext)

  const {
    setIsLoading,
    isLoading,
    tier,
    interval,
    currentSubscription,
    submitType,
  } = context
  const { handleCheckout } = useCheckout()
  const { handleUpdate } = useUpdate()
  const recurly = useRecurly()

  const [paypal, setPaypal] = useState<PayPalInstance | null>(null)
  const [isReady, setIsReady] = useState(false)

  const disabled = isLoading || !paypal || payPalDisabledFeature?.isEnabled

  const handlePaymentToken = (data: unknown) => {
    if (isRecurlyPayment(data)) {
      setIsLoading(true)
      const handlePaymentCallBack =
        submitType === CHECKOUT_SUBMIT_TYPE.update
          ? handleUpdate
          : handleCheckout
      handlePaymentCallBack({
        token: data,
        checkoutSuccessSource,
        paymentMethod: MIXPANEL_PROPS.BUTTON_NAME.values.PAYPAL,
      })
    } else {
      console.error('Invalid payment token received:', data)
    }
  }

  const handlePaymentError = (data: unknown) => {
    if (isPayPalError(data)) {
      // @ts-ignore
      Mixpanel.trackPaymentSubmitClick({
        tier,
        planInterval: interval,
        paymentMethod: MIXPANEL_PROPS.PAYMENT_METHOD.values.PAYPAL,
        paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.PAYMENT_ISSUE,
        paymentIssue: `${data.code}: ${data.message}`,
        code: data.code,
        message: data.message,
        subscription: currentSubscription,
      })
      setIsLoading(false)
      console.error('PayPal Error:', data)
    } else {
      console.error('Invalid PayPal error received:', data)
    }
  }

  const handlePaymentCancel = () => {
    setIsLoading(false)
  }

  useEffect(() => {
    if (paypal) {
      paypal.off('error', handlePaymentError)
      paypal.off('token', handlePaymentToken)
      paypal.off('cancel', handlePaymentCancel)
    }

    const paypalInstance = recurly.PayPal({
      display: { displayName: 'Ubersuggest' },
    })

    paypalInstance.on('error', handlePaymentError)
    paypalInstance.on('token', handlePaymentToken)
    paypalInstance.on('cancel', handlePaymentCancel)

    setPaypal(paypalInstance)
    setIsReady(true)
  }, [
    interval,
    tier,
    currentSubscription?.planLabel,
    currentSubscription?.unitPrice,
  ])

  const handlePayPalClick = () => {
    if (!isReady || isLoading || !paypal || payPalDisabledFeature?.isEnabled)
      return
    setIsLoading(true)
    Mixpanel.trackPaymentButtonClicked({
      tier,
      planInterval: interval,
      buttonName: MIXPANEL_PROPS.BUTTON_NAME.values.PAYPAL,
      source,
    })
    paypal.start()
  }

  if (!isReady) return null

  return (
    <PaymentMethodWrapper
      disabledByGB={payPalDisabledFeature?.isEnabled}
      text="india_paypal_helper_message"
    >
      <Button
        data-testid={testId || 'paypal-button'}
        disabled={disabled}
        variant="legacy"
        className={cn(buttonVariants({ variant, className }))}
        onClick={handlePayPalClick}
      >
        <PayPalLogo theme={theme} />
      </Button>
    </PaymentMethodWrapper>
  )
}
